import {
  Component, OnInit, OnDestroy, HostListener,
  ChangeDetectorRef, NgZone
} from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { ChatStateService } from '../../store/chat-state.service';
import { ChatConfigService } from '../../services/chat-config.service';

/** Width of the sidebar panel — must match .xcs-panel width in chat-sessions */
const SIDEBAR_W    = 230;
/** Default chat-area width when no sidebar is open */
const CHAT_DEFAULT = 660;
/** Minimum width the user can drag to */
const CHAT_MIN     = 360;
/** Maximum width the user can drag to */
const CHAT_MAX     = 1100;

/**
 * Public/login routes where the chatbot must NEVER appear,
 * even if a stale token is still present in localStorage.
 */
const PUBLIC_ROUTES = ['/login', '/', '/forgot-password', '/reset-password'];

@Component({
  selector: 'app-chatbot',
  template: `
    <!--
      THREE-GATE VISIBILITY GUARD
      ════════════════════════════════════════════════════════════
      Gate 1 — env.js flag:    window.__env.chatbotEnabled = true
        Controls per-customer visibility at deployment time.
        Missing / false  →  hidden  (safe opt-in default).

      Gate 2 — Authentication: kuzzle token present in localStorage
        Hides on login page and any page before the user signs in.
        Detected every 1 s so logout (localStorage.clear) is caught
        in the same tab without needing a page reload.

      Gate 3 — Route guard: not a public/login route
        Even if a stale token exists in localStorage, the chatbot
        stays hidden on /login and other public pages.

      ALL THREE must pass for anything to render.
      ════════════════════════════════════════════════════════════
    -->
    <ng-container *ngIf="isChatbotEnabled && isAuthenticated && !isPublicRoute">

      <!-- Floating Action Button -->
      <button
        class="xcb-fab"
        [class.xcb-fab--open]="isOpen"
        [class.xcb-fab--streaming]="isStreaming"
        [class.xcb-fab--hidden]="isFullscreen"
        (click)="chatState.toggle()"
        title="AI Assistant"
        aria-label="Open XEDGE AI Assistant">

        <svg *ngIf="isOpen" viewBox="0 0 20 20" fill="currentColor" width="22" height="22">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>
        </svg>
        <svg *ngIf="!isOpen" viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
          <path fill-rule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z" clip-rule="evenodd"/>
        </svg>

        <span class="xcb-pulse" *ngIf="isStreaming"></span>
        <span class="xcb-brand" *ngIf="!isOpen">AI</span>
      </button>

      <!-- Chat panel -->
      <div class="xcb-panel"
           *ngIf="isOpen"
           [class.xcb-panel--fullscreen]="isFullscreen"
           [class.xcb-panel--dragging]="isDragging"
           [style.width.px]="isFullscreen ? null : effectiveWidth">

        <div class="xcb-resize-handle"
             *ngIf="!isFullscreen"
             (mousedown)="startResize($event)"
             title="Drag to resize">
          <span class="xcb-resize-grip"></span>
        </div>

        <app-chat-window></app-chat-window>
      </div>

    </ng-container>
  `,
  styles: [`
    :host {
      position: fixed;
      bottom: 24px; right: 24px;
      z-index: 99999;
      font-family: -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }

    /* FAB */
    .xcb-fab {
      position: relative;
      width: 56px; height: 56px; border-radius: 50%;
      background: linear-gradient(135deg, #4f7ef7 0%, #6366f1 60%, #7c3aed 100%);
      border: none; cursor: pointer; color: #fff;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 4px 20px rgba(79,126,247,.45), 0 1px 4px rgba(0,0,0,.1);
      transition: transform .3s cubic-bezier(.175,.885,.32,1.275), box-shadow .2s, opacity .2s;
      outline: none;
    }
    .xcb-fab:hover      { transform: scale(1.1); box-shadow: 0 8px 32px rgba(79,126,247,.55); }
    .xcb-fab--open      { transform: scale(.94); }
    .xcb-fab--hidden    { opacity: 0; pointer-events: none; }
    .xcb-fab--streaming { animation: xcFabGlow 2s ease-in-out infinite; }
    @keyframes xcFabGlow {
      0%,100% { box-shadow: 0 4px 20px rgba(79,126,247,.45); }
      50%     { box-shadow: 0 4px 32px rgba(79,126,247,.8), 0 0 0 6px rgba(79,126,247,.15); }
    }
    .xcb-pulse {
      position: absolute; inset: -5px; border-radius: 50%;
      border: 2px solid rgba(79,126,247,.5);
      animation: xcPulseRing 1.6s ease-out infinite;
    }
    @keyframes xcPulseRing { 0% { transform:scale(1);opacity:1; } 100% { transform:scale(1.6);opacity:0; } }
    .xcb-brand {
      position: absolute; top: -4px; right: -4px;
      background: linear-gradient(135deg,#0ea5e9,#06b6d4);
      color: #fff; font-size: 8px; font-weight: 800;
      width: 18px; height: 18px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid #fff;
    }

    /* Panel */
    .xcb-panel {
      position: absolute;
      bottom: 68px; right: 0;
      height: 700px;
      max-height: calc(100vh - 110px);
      min-width: 360px;
      border-radius: 18px;
      box-shadow: 0 25px 60px rgba(0,0,0,.18), 0 8px 24px rgba(0,0,0,.08), 0 0 0 1px rgba(0,0,0,.04);
      overflow: hidden;
      transition: width .22s ease, border-radius .22s ease;
      animation: xcPanelIn .28s cubic-bezier(.175,.885,.32,1.275);
    }
    .xcb-panel--dragging { transition: none !important; }
    @keyframes xcPanelIn {
      from { opacity:0; transform:scale(.88) translateY(22px); transform-origin:bottom right; }
      to   { opacity:1; transform:scale(1)   translateY(0); }
    }

    /* Fullscreen */
    .xcb-panel--fullscreen {
      position: fixed !important;
      inset: 0 !important;
      width: 100vw !important; height: 100vh !important;
      max-height: 100vh !important;
      border-radius: 0 !important; box-shadow: none !important;
      animation: xcFsIn .22s cubic-bezier(.4,0,.2,1);
    }
    @keyframes xcFsIn {
      from { opacity:.7; transform:scale(.97); }
      to   { opacity:1;  transform:scale(1); }
    }

    /* Drag-resize handle */
    .xcb-resize-handle {
      position: absolute; left: 0; top: 0;
      width: 8px; height: 100%;
      cursor: ew-resize; z-index: 10;
      display: flex; align-items: center; justify-content: center;
      transition: background .15s;
    }
    .xcb-resize-handle:hover { background: rgba(99,102,241,.08); }
    .xcb-resize-grip {
      width: 2px; height: 36px; border-radius: 99px;
      background: #6366f1; opacity: 0;
      box-shadow: -4px 0 0 #6366f1, 4px 0 0 #6366f1;
      transition: opacity .15s;
    }
    .xcb-resize-handle:hover .xcb-resize-grip,
    .xcb-panel--dragging .xcb-resize-grip { opacity: 1; }

    /* Mobile */
    @media (max-width: 768px) {
      :host { bottom: 12px; right: 12px; }
      .xcb-panel:not(.xcb-panel--fullscreen) {
        position: fixed !important;
        bottom: 78px; right: 8px; left: 8px;
        width: auto !important;
        height: calc(100dvh - 100px);
        max-height: calc(100dvh - 100px);
      }
      .xcb-resize-handle { display: none; }
    }
  `]
})
export class ChatbotComponent implements OnInit, OnDestroy {

  // ── UI state ──────────────────────────────────────────────────────────────
  isOpen       = false;
  isStreaming  = false;
  sidebarOpen  = false;
  isFullscreen = false;

  // ── Gate 1: env.js feature flag ───────────────────────────────────────────
  /** Reads window.__env.chatbotEnabled on every CD cycle. No state needed. */
  get isChatbotEnabled(): boolean {
    return this.config.isChatbotEnabled;
  }

  // ── Gate 2: authentication ────────────────────────────────────────────────
  /**
   * True only while a Kuzzle token exists in localStorage.
   * Updated by a 1-second poll so same-tab localStorage.clear() (sign-out)
   * is caught without needing a page reload.
   */
  isAuthenticated = false;

  // ── Gate 3: route guard ───────────────────────────────────────────────────
  /**
   * True while the current URL is a public / pre-login route.
   * Prevents the FAB showing on /login even when a stale token
   * is still sitting in localStorage from a previous session.
   */
  get isPublicRoute(): boolean {
    const url = (this.router.url || '/').split('?')[0].split('#')[0];
    return PUBLIC_ROUTES.some(r => url === r || url.startsWith(r + '/'));
  }

  // ── Resize ────────────────────────────────────────────────────────────────
  basePanelWidth = CHAT_DEFAULT;

  get effectiveWidth(): number {
    return this.basePanelWidth + (this.sidebarOpen ? SIDEBAR_W : 0);
  }

  isDragging        = false;
  private dragStartX    = 0;
  private dragStartBase = 0;

  // ── Internals ─────────────────────────────────────────────────────────────
  private sub!:      Subscription;
  private routeSub!: Subscription;
  /**
   * Always-on 1-second poll.
   * Catches same-tab localStorage.clear() (sign-out) which does NOT
   * fire the window:storage event in the same window.
   */
  private authPoll!: ReturnType<typeof setInterval>;

  constructor(
    public  chatState: ChatStateService,
    private config:    ChatConfigService,
    private router:    Router,
    private cdr:       ChangeDetectorRef,
    private zone:      NgZone
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Immediate check on load
    this.isAuthenticated = !!this.config.kuzzleToken;

    // Always-on poll — handles same-tab localStorage.clear() on sign-out
    // and same-tab localStorage.setItem on sign-in.
    // 1 s interval: fast enough to feel instant, cheap enough to run always.
    this.authPoll = setInterval(() => this._syncAuth(), 1000);

    // Re-evaluate on every route change so the FAB hides immediately
    // when the router navigates to /login (e.g. after sign-out redirect).
    this.routeSub = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => {
        this._syncAuth();
        this.cdr.detectChanges();
      });

    // Core state subscription
    this.sub = this.chatState.state$.subscribe(s => {
      this.zone.run(() => {
        this.isOpen       = s.isOpen;
        this.isStreaming  = s.isStreaming;
        this.sidebarOpen  = s.isSidebarOpen;
        this.isFullscreen = s.isFullscreen;
        this.cdr.detectChanges();
      });
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    clearInterval(this.authPoll);
    this.cleanupDrag();
  }

  // ── Auth sync ─────────────────────────────────────────────────────────────

  /**
   * Cross-tab immediate response.
   * window:storage fires when ANOTHER tab writes/removes localStorage.
   * Same-tab changes are caught by the always-on poll above.
   */
  @HostListener('window:storage', ['$event'])
  onStorageChange(e: StorageEvent): void {
    if (e.key === 'token' || e.key === null) {
      this._syncAuth();
    }
  }

  /**
   * Single source of auth truth.
   * Reads the token from localStorage every time it's called.
   * If the state changed, updates isAuthenticated and closes the
   * panel if the user just signed out.
   */
  private _syncAuth(): void {
    const nowAuth = !!this.config.kuzzleToken;

    if (nowAuth === this.isAuthenticated) return;   // nothing changed

    this.zone.run(() => {
      this.isAuthenticated = nowAuth;

      if (!nowAuth && this.isOpen) {
        // Token gone → close the panel immediately
        this.chatState.close();
      }

      this.cdr.detectChanges();
    });
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isFullscreen) {
      this.chatState.exitFullscreen();
    } else if (this.isOpen) {
      this.chatState.close();
    }
  }

  // ── Drag-resize ───────────────────────────────────────────────────────────

  startResize(e: MouseEvent): void {
    e.preventDefault();
    this.isDragging       = true;
    this.dragStartX       = e.clientX;
    this.dragStartBase    = this.basePanelWidth;
    document.body.style.userSelect = 'none';
    document.body.style.cursor     = 'ew-resize';
    this.cdr.detectChanges();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;
    const delta    = this.dragStartX - e.clientX;
    const newWidth = Math.max(CHAT_MIN, Math.min(CHAT_MAX, this.dragStartBase + delta));
    this.basePanelWidth = newWidth;
    this.cdr.detectChanges();
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (this.isDragging) {
      this.cleanupDrag();
      this.cdr.detectChanges();
    }
  }

  private cleanupDrag(): void {
    this.isDragging                = false;
    document.body.style.userSelect = '';
    document.body.style.cursor     = '';
  }
}